import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Gamepad2, BarChart3, Star, Users, Award, Mail, Instagram, Youtube, Music, Crown, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const Home = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: BookOpen,
      title: 'Rich Vocabulary',
      description: 'Learn Algerian words with pronunciation guides and example sentences',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Gamepad2,
      title: 'Interactive Games',
      description: 'Master the language through flashcards, quizzes, and memory games',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: BarChart3,
      title: 'Track Progress',
      description: 'Monitor your learning journey with detailed progress analytics',
      color: 'from-green-600 to-red-600'
    }
  ];

  const stats = [
    { icon: Users, value: '500+', label: 'Active Learners' },
    { icon: BookOpen, value: '1000+', label: 'Vocabulary Words' },
    { icon: Award, value: '95%', label: 'Success Rate' }
  ];

  return (
    <div className="min-h-screen">
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-green-600 to-red-600 text-white py-3 px-4 text-center">
        <p className="text-sm md:text-base font-semibold">
          🎉 Use code <span className="font-bold bg-white text-green-600 px-2 py-1 rounded">OPENING50</span> for 50% off your first month! 🎉
        </p>
      </div>
      
      {/* Hero Section */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-medium mb-6 font-heading text-gray-700">
                It's time to learn to
              </h2>
              <div className="flex justify-center">
                <img 
                  src="/speak-algerian-logo.png" 
                  alt="Speak Algerian" 
                  className="h-32 md:h-48 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
              Learn the Algiers dialect with interactive games, comprehensive vocabulary, and personalized progress tracking.
            </p>
            
            <div className="mb-10">
              <div className="inline-block bg-gradient-to-r from-green-100 to-red-100 px-6 py-3 rounded-full border border-green-200">
                <p className="text-base text-gray-700 font-medium">
                  Brought to you by Jamilah, the derja teacher you all know and love ✨
                </p>
              </div>
            </div>
            
            <p className="text-lg text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed">
              Whether it's to connect with <strong>family</strong>, communicate with <strong>business</strong> prospects, or connect to your <strong>roots</strong>, learning Algerian derja has never been more easy.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            {isAuthenticated ? (
              <>
                <Link to="/vocabulary">
                  <Button size="lg" className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200">
                    Start Learning
                  </Button>
                </Link>
                <Link to="/games">
                  <Button variant="outline" size="lg" className="border-2 border-green-600 text-green-700 hover:bg-green-50 px-8 py-3 text-lg font-semibold transition-all duration-200">
                    Play Games
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/pricing">
                  <Button size="lg" className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200">
                    <Crown className="w-5 h-5 mr-2" />
                    Subscribe Now
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" className="border-2 border-green-600 text-green-700 hover:bg-green-50 px-8 py-3 text-lg font-semibold transition-all duration-200">
                    Sign In
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-100 to-red-100 rounded-full mb-4">
                    <IconComponent className="w-6 h-6 text-green-700" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
              Why Choose Speak Algerian?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our comprehensive approach combines traditional learning methods with modern technology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                  <CardContent className="p-6 text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.color} rounded-full mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 font-heading">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of learners discovering the beauty of Algerian dialect. 
            Start with our interactive vocabulary builder today.
          </p>
          <Link to="/vocabulary">
            <Button size="lg" className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white px-10 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200">
              Begin Learning Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;